import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  Check, Headphones, Crown, Zap, Star, ArrowRight, Loader2, MapPin,
  Sparkles, Disc3, Music2, Users, Camera, Coffee, Wifi
} from "lucide-react";
import { DjRoomPerks } from "@/components/DjRoomPerks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

// DJ Room photos (sourced from Gallery)
const DJ_PHOTOS = [
  { url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/o4aghb0q_DSC_2699.jpg", alt: "DJ Room Decks" },
  { url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/9c2fw01q_DSC_2674.jpg", alt: "DJ Setup" },
  { url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/irjscrt2_DSC_0145.jpg", alt: "DJ Room Lounge" },
  { url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/5hedikfg_DSC_2648.jpg", alt: "DJ Room Equipment" },
  { url: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/r9ezf2ho_DSC_2727.jpg", alt: "DJ Room View" },
];

const DJ_TIERS = [
  {
    id: "dj_member",
    name: "DREAM MAKERS DJ MEMBER",
    badge: "BRONZE",
    badgeIcon: "🥉",
    price: 99,
    cadence: "/month",
    icon: Zap,
    accent: "from-amber-700 via-orange-600 to-amber-500",
    border: "border-amber-600/30",
    ring: "ring-amber-500/40",
    buttonText: "text-white",
    tagline: "Perfect for casual DJs who want regular practice time.",
    value: "$120+",
    savings: "$21+",
    features: [
      "4 Hours DJ Room Access Per Month",
      "Free Water & Soft Drinks Included",
      "Priority Booking Over Casual Users",
      "Unused Hours Roll Over for 30 Days",
      "10% Off All Dream Makers Services",
      "Access to the Dream Makers DJ Community",
      "Member-Only Giveaways & Promotions",
    ],
  },
  {
    id: "pro_dj_member",
    name: "DREAM MAKERS PRO DJ MEMBER",
    badge: "PRO",
    badgeIcon: "🥈",
    price: 240,
    cadence: "/month",
    icon: Star,
    accent: "from-slate-300 via-zinc-300 to-slate-400",
    border: "border-zinc-300/40",
    ring: "ring-zinc-300/50",
    buttonText: "text-black",
    tagline: "For DJs actively developing their skills, content, and brand.",
    value: "$350+",
    savings: "$110+",
    popular: true,
    features: [
      "10 Hours DJ Room Access Per Month",
      "Free Water & Soft Drinks Included",
      "Priority Booking Access",
      "Unused Hours Roll Over for 30 Days",
      "15% Off All Dream Makers Services",
      "15% Off DJ Press Shots",
      "1 Free DJ Content Clip Filmed in the DJ Room Each Month",
      "Access to the Dream Makers DJ Community",
      "Networking Opportunities & Member-Only Events",
      "Exclusive Promotions & Giveaways",
    ],
  },
  {
    id: "elite_dj_member",
    name: "DREAM MAKERS ELITE DJ MEMBER",
    badge: "ELITE",
    badgeIcon: "🥇",
    price: 350,
    cadence: "/month",
    icon: Crown,
    accent: "from-yellow-400 via-amber-500 to-orange-500",
    border: "border-yellow-500/40",
    ring: "ring-yellow-500/40",
    buttonText: "text-black",
    tagline: "Built for serious DJs, artists, and content creators who want maximum access, exposure, and opportunities.",
    value: "$600+",
    savings: "$250+",
    features: [
      "16 Hours DJ Room Access Per Month",
      "Free Water & Soft Drinks Included",
      "Priority Booking Access",
      "Unused Hours Roll Over for 30 Days",
      "20% Off Additional DJ Room Bookings",
      "20% Off All Dream Makers Services",
      "2 Free DJ Mix Recording Sessions Per Month",
      "1 Professional Content Package Every 3 Months (Photos/Reels)",
      "First Access to Dream Makers Events & Opportunities",
      "Featured Across Dream Makers Social Media Platforms",
      "Access to the Dream Makers DJ Community",
      "Exclusive Member Promotions & Giveaways",
    ],
  },
];

const INCLUDED_PERKS = [
  { icon: Headphones, label: "Melbourne's cheapest professional DJ room" },
  { icon: Disc3, label: "3 × Pioneer CDJ-3000s" },
  { icon: Music2, label: "Pioneer DJM-A9 Mixer" },
  { icon: Sparkles, label: "Professional Sound System" },
  { icon: Camera, label: "Sound-Treated DJ Room" },
  { icon: Coffee, label: "Free Water & Soft Drinks" },
  { icon: Wifi, label: "Fast WiFi" },
  { icon: Headphones, label: "Sennheiser HD 25 Headphones Free to Use" },
  { icon: Users, label: "Dream Makers DJ Community Access" },
  { icon: MapPin, label: "Located in Tullamarine, Melbourne" },
];

const TIER_LABELS_DJ = {
  dj_member: "Dream Makers DJ Member — $99/month",
  pro_dj_member: "Dream Makers Pro DJ Member — $240/month",
  elite_dj_member: "Dream Makers Elite DJ Member — $350/month",
  undecided: "Not sure yet — recommend one for me",
};

export default function DjMembership() {
  const [form, setForm] = useState({
    full_name: "",
    dj_name: "",
    email: "",
    phone: "",
    tier: "",
    instagram: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.tier) {
      toast.error("Please fill in your name, email, phone and selected tier.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/sessions-membership/interest`, form);
      setSubmitted(true);
      toast.success("Application received — we'll contact you within 24 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectTier = (tierId) => {
    setForm({ ...form, tier: tierId });
    document.getElementById("dj-membership-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-dream-bg text-white" data-testid="dj-membership-page">
      {/* HERO */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Hero background image */}
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `linear-gradient(rgba(10,10,10,0.65), rgba(10,10,10,0.95)), url(${DJ_PHOTOS[0].url})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-dream-violet/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 -right-32 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full bg-dream-violet/10 border border-dream-violet/30">
              <Headphones className="w-4 h-4 text-dream-violet" />
              <span className="text-xs font-mono uppercase tracking-widest text-dream-violet">DJ Room · Memberships</span>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Dream Makers
              <br />
              <span className="bg-gradient-to-r from-dream-violet via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Sessions Memberships
              </span>
            </h1>
            <p className="text-white/60 text-lg sm:text-xl max-w-2xl mx-auto mb-8 leading-relaxed">
              Practice. Create. Connect. Grow. Lock in your spot in Melbourne&apos;s most affordable
              professional DJ practice space.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 text-xs uppercase tracking-wider font-mono text-white/40">
              <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">3-Month Minimum</span>
              <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Hours Roll Over 30 Days</span>
              <span className="px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300">CDJ-3000s · DJM-A9</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PHOTO GALLERY STRIP */}
      <section className="px-6 pb-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {DJ_PHOTOS.map((photo, idx) => (
              <motion.div
                key={photo.url}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: idx * 0.06 }}
                className="relative aspect-square rounded-2xl overflow-hidden border border-white/5 group"
                data-testid={`dj-photo-${idx}`}
              >
                <img
                  src={photo.url}
                  alt={photo.alt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <p className="absolute bottom-2 left-3 text-xs font-mono uppercase tracking-wider text-white/80">{photo.alt}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pt-10">
        <div className="max-w-7xl mx-auto">
          <DjRoomPerks testId="membership-dj-room-perks" />
        </div>
      </section>

      {/* TIERS */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent via-dream-violet/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <p className="text-dream-violet font-mono text-xs tracking-widest mb-4 uppercase">Choose Your Tier</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold">Three Levels of Access.</h2>
            <p className="text-white/60 mt-4 max-w-xl mx-auto">
              Save more every month, the deeper you commit. All tiers include the DJ Community + free drinks.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {DJ_TIERS.map((tier, idx) => {
              const Icon = tier.icon;
              return (
                <motion.div
                  key={tier.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className={`relative flex flex-col rounded-3xl border ${tier.border} bg-dream-surface/40 backdrop-blur-sm p-8 ${tier.popular ? "ring-2 " + tier.ring + " md:scale-105 md:-mt-2" : ""}`}
                  data-testid={`dj-tier-card-${tier.id}`}
                >
                  {tier.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-dream-violet text-white text-xs font-bold uppercase tracking-wider">
                      Most Popular
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tier.accent} flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-2xl" aria-hidden="true">{tier.badgeIcon}</span>
                  </div>

                  <h3 className="font-heading text-lg font-bold tracking-wide mb-1 leading-snug">{tier.name}</h3>
                  <p className="text-white/40 text-sm mb-6 leading-relaxed min-h-[40px]">{tier.tagline}</p>

                  <div className="mb-2 flex items-baseline gap-1">
                    <span className="text-5xl font-bold tracking-tight">${tier.price}</span>
                    <span className="text-white/40 text-base">{tier.cadence}</span>
                  </div>
                  <div className="mb-6 flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/60">
                      Value: <span className="text-white font-semibold">{tier.value}</span>
                    </span>
                    <span className="px-2 py-1 rounded-full bg-green-500/10 border border-green-500/30 text-green-400">
                      Save {tier.savings}/mo
                    </span>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-white/75">
                        <Check className="w-5 h-5 text-dream-violet flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => selectTier(tier.id)}
                    className={`w-full bg-gradient-to-r ${tier.accent} hover:opacity-90 ${tier.buttonText} font-semibold py-6 text-base group`}
                    data-testid={`select-dj-tier-${tier.id}`}
                  >
                    Apply for {tier.badge}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <p className="text-dream-violet font-mono text-xs tracking-widest mb-4 uppercase">All Memberships Include</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold">Everything you need to level up.</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {INCLUDED_PERKS.map((perk, idx) => {
              const Icon = perk.icon;
              return (
                <motion.div
                  key={perk.label}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ duration: 0.4, delay: idx * 0.04 }}
                  className="p-5 rounded-2xl bg-dream-surface/40 border border-white/5 hover:border-dream-violet/40 transition-colors"
                >
                  <Icon className="w-6 h-6 text-dream-violet mb-3" />
                  <p className="text-white/85 text-sm font-medium leading-snug">{perk.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MEMBERSHIP TERMS */}
      <section className="py-20 px-6 bg-gradient-to-br from-dream-violet/10 via-purple-900/5 to-cyan-500/5">
        <div className="max-w-4xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <p className="text-dream-violet font-mono text-xs tracking-widest mb-4 uppercase">Membership Terms</p>
            <h2 className="font-heading text-3xl font-bold mb-8">How it works.</h2>

            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "3 Month Minimum Commitment",
                "Hours Reset Monthly",
                "Unused Hours Roll Over For 30 Days",
                "Additional Hours At Member Discount Rates",
                "Membership Benefits Are Non-Transferable",
                "Auto-renews after the 3-month term",
              ].map((term) => (
                <div key={term} className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/5">
                  <Check className="w-5 h-5 text-dream-violet flex-shrink-0 mt-0.5" />
                  <span className="text-white/80 text-sm leading-relaxed">{term}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="dj-membership-form" className="py-20 px-6 bg-dream-bg border-t border-dream-border">
        <div className="max-w-2xl mx-auto">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center p-10 rounded-3xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30"
              data-testid="dj-membership-success"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="font-heading text-3xl font-bold mb-4">Application Received</h3>
              <p className="text-white/70 mb-2">
                Thanks for applying to a Dream Makers Sessions membership. We&apos;ll contact you within 24 hours to get you set up.
              </p>
              <p className="text-white/40 text-sm">A confirmation has been sent to {form.email}.</p>
            </motion.div>
          ) : (
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
              <div className="text-center mb-10">
                <p className="text-dream-violet font-mono text-xs tracking-widest mb-4 uppercase">Apply Now</p>
                <h2 className="font-heading text-4xl font-bold mb-3">Lock in your membership.</h2>
                <p className="text-white/60">
                  Fill out the form below and we&apos;ll contact you within 24 hours to get you started.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="dj-membership-form-element">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Full Name *</Label>
                    <Input
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      required
                      className="mt-2 bg-dream-surface/40 border-dream-border text-white"
                      data-testid="dj-full-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">DJ / Stage Name</Label>
                    <Input
                      value={form.dj_name}
                      onChange={(e) => setForm({ ...form, dj_name: e.target.value })}
                      className="mt-2 bg-dream-surface/40 border-dream-border text-white"
                      data-testid="dj-name-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 text-sm">Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      className="mt-2 bg-dream-surface/40 border-dream-border text-white"
                      data-testid="dj-email-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 text-sm">Phone *</Label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      required
                      className="mt-2 bg-dream-surface/40 border-dream-border text-white"
                      data-testid="dj-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white/70 text-sm">Instagram (optional)</Label>
                  <Input
                    placeholder="@yourhandle"
                    value={form.instagram}
                    onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                    className="mt-2 bg-dream-surface/40 border-dream-border text-white"
                    data-testid="dj-instagram-input"
                  />
                </div>

                <div>
                  <Label className="text-white/70 text-sm">Which tier? *</Label>
                  <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                    <SelectTrigger className="mt-2 bg-dream-surface/40 border-dream-border text-white" data-testid="dj-tier-select">
                      <SelectValue placeholder="Select a membership tier" />
                    </SelectTrigger>
                    <SelectContent className="bg-dream-surface border-dream-border text-white">
                      {Object.entries(TIER_LABELS_DJ).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-white/70 text-sm">Tell us about your DJ journey (optional)</Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={4}
                    placeholder="What style do you play? Goals, gigs, what you want from the membership..."
                    className="mt-2 bg-dream-surface/40 border-dream-border text-white"
                    data-testid="dj-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-dream-violet to-purple-600 hover:opacity-90 text-white font-semibold py-6 text-base"
                  data-testid="dj-membership-submit"
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <>Submit Application <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>

                <p className="text-center text-white/40 text-xs">
                  We&apos;ll reach out within 24 hours to confirm your tier and set up payment.
                </p>
              </form>
            </motion.div>
          )}
        </div>
      </section>

      {/* TAGLINE FOOTER */}
      <section className="py-12 px-6 text-center border-t border-dream-border">
        <p className="font-heading text-2xl sm:text-3xl font-bold text-white/70">
          DREAM MAKERS SESSIONS
        </p>
        <p className="text-white/40 mt-2 font-mono text-sm tracking-widest">
          PRACTICE · CREATE · CONNECT · GROW
        </p>
      </section>
    </div>
  );
}
